import { yupResolver } from '@hookform/resolvers/yup';
import { formatISO } from 'date-fns';
import { useTranslation } from 'next-i18next';
import { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import {
  useAddNewWarehouseProduct,
  useEditInboundDelivery,
  useGetInboundDeliveryBatches,
  useGetInboundDeliveryGeneralInfo,
} from '@/modules/warehouseManagement/shared/apiHooks';
import { IWarehouseManagement } from '@/modules/warehouseManagement/shared/types';
import { useCreateInboundDeliveryUtils } from '@/modules/warehouseManagement/warehouses/components/InboundDelivery/CreateEditInboundDelivery/logic';
import ConfirmationModal from '@/shared/components/molecules/ConfirmationModal';
import MultiStepForm from '@/shared/components/molecules/MultiStepForm';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';

const CreateEditInboundDelivery: React.FC<{
  editId?: number;
  onInboundDeliveryCreated?: () => void;
}> = ({ editId, onInboundDeliveryCreated }) => {
  const { t } = useTranslation();

  const [selectedAsset] = useStore((store) => store?.asset);

  const { openModal } = useModal();

  const {
    steps,
    CreateEditInboundDeliverySchema,
    getMutationObject,
    getDefaultValues,
    getEditMutation,
  } = useCreateInboundDeliveryUtils();

  const { mutate: create, isLoading: isCreating } = useAddNewWarehouseProduct();
  const { mutate: edit, isLoading: isEditing } = useEditInboundDelivery();

  // get default values if editing
  const { data: generalInfo, isFetching: isLoadingGeneralInfo } =
    useGetInboundDeliveryGeneralInfo({
      entityId: selectedAsset?.id,
      inboundDeliveryId: editId,
    });
  const { data: inputs, isFetching: isLoadingBatches } =
    useGetInboundDeliveryBatches({
      entityId: selectedAsset?.id,
      inboundDeliveryId: editId,
    });

  const formMethods =
    useForm<IWarehouseManagement.ICreateInboundDeliveryFormValues>({
      defaultValues: {
        depositOrderDate: editId
          ? undefined
          : formatISO(new Date(), { representation: 'date' }),
        products: [{}],
      },
      resolver: yupResolver(CreateEditInboundDeliverySchema),
      mode: 'all',
    });

  const onSuccess = () =>
    openModal(
      <ConfirmationModal
        variant='success'
        header={t(`${editId ? 'edit' : 'create'}-inbound-delivery-success`)}
        onDismissRemoveNumber={2}
        autoDismissAfter={5000}
      />,
      {
        dismissable: false,
      }
    );

  const onSubmit: SubmitHandler<
    IWarehouseManagement.ICreateInboundDeliveryFormValues
  > = (values) => {
    if (editId) {
      edit(
        {
          entityId: selectedAsset?.id,
          inboundDeliveryId: generalInfo?.id,
          values: getEditMutation(formMethods.watch(), {
            ...(generalInfo as IWarehouseManagement.IInboundDeliveryGeneralInfo),
            products: inputs as IWarehouseManagement.IInboundDeliveryBatch[],
          }),
        },
        { onSuccess }
      );
    } else {
      create(
        { values: getMutationObject(values), entityId: selectedAsset?.id },
        {
          onSuccess: () => {
            onSuccess();
            onInboundDeliveryCreated?.();
          },
        }
      );
    }
  };

  useEffect(() => {
    if (generalInfo && inputs)
      formMethods.reset({
        ...getDefaultValues({
          ...generalInfo,
          products: inputs,
        }),
        editId,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generalInfo, inputs]);

  return (
    <MultiStepForm<IWarehouseManagement.ICreateInboundDeliveryFormValues>
      steps={steps}
      formMethods={formMethods}
      onSubmit={onSubmit}
      isMutating={isCreating || isEditing}
      isLoading={isLoadingGeneralInfo || isLoadingBatches}
      submitButtonLabel={t(editId ? 'save-edits' : 'add-new-stock')}
    />
  );
};

export default CreateEditInboundDelivery;
